/**
 * Somebody else's findings: the quarantine, on screen.
 *
 * Split from `findingsList.tsx`, which is the reader's *own* list. The seam is
 * the same one the whole import feature rests on — whose work this is — and it
 * had already been drawn in that file as a comment ("their own section rather
 * than a column on the main list, because the distinction is not an attribute of
 * a row"). A section arguing that imports are a different subject, living inside
 * the file about the reader's findings, was making the opposite case in its
 * layout.
 *
 * What is here: the per-author sections on the findings page, the feeds being
 * followed, and the card that lists one author's import. How findings arrive —
 * the upload and the follow form — is `bringIn.tsx`. Everything here is a
 * read-only view by construction: an imported finding cannot be edited, and
 * the only actions offered are putting an import back down or unfollowing.
 * Adopting one is on the finding itself, where the claim being adopted is in
 * front of the reader.
 */

import { useState, type JSX } from 'react'

import { describeFailure } from '@shared/user-errors.js'

import { Failure, Loading, PAGE_SIZE, citationLine, useLoaded } from './cardDisplay'
import type { FindingPage, ImportSummary, Subscription } from '../account'
import {
  claimImport,
  deleteImport,
  listFindings,
  listImports,
  listSubscriptions,
  unsubscribeFromFeed,
} from '../account'
import type { Card } from '../cards'
import { t } from '../i18n'

/**
 * Another author's findings, kept visibly apart.
 *
 * Their own section rather than a column on the main list, because the
 * distinction is not an attribute of a row — it is whose work it is, and the
 * moment the two are interleaved a reader can no longer tell at a glance.
 */
export function ImportedSections({
  corpus,
  onOpen,
  onChanged,
}: {
  corpus: string | null
  onOpen: (card: Card) => void
  onChanged: () => void
}): JSX.Element | null {
  const { state, reload } = useLoaded<ImportSummary[]>(
    () => listImports(corpus ?? undefined),
    `imports:${corpus}`,
  )
  // Shown after the sections rather than in one: a claimed import's section is gone.
  const [claimMessage, setClaimMessage] = useState<{ isDone: boolean; text: string } | null>(null)
  if (state.status !== 'ready') return null
  if (state.data.length === 0 && claimMessage === null) return null

  async function drop(author: string) {
    if (!window.confirm(t('imports.confirmRemove', { author }))) return
    await deleteImport(author)
    reload()
  }

  /**
   * "This author is me." Asked, because it cannot be undone from here and it is
   * the one claim about authorship this product takes on the reader's word.
   */
  async function claim(author: string, count: number) {
    if (!window.confirm(t('imports.confirmClaim', { author, count }))) return
    setClaimMessage(null)
    try {
      const claimed = await claimImport(author)
      setClaimMessage({ isDone: true, text: t('imports.claimed', { author, count: claimed }) })
      reload()
      onChanged()
    } catch (error) {
      setClaimMessage({ isDone: false, text: describeFailure(error, t('imports.claimFailed')) })
    }
  }

  return (
    <>
      {state.data.map((entry) => (
        <ImportedSection
          key={entry.author}
          entry={entry}
          onOpen={onOpen}
          onClaim={() => void claim(entry.author, entry.findings)}
          onRemove={() => void drop(entry.author)}
        />
      ))}
      {claimMessage !== null &&
        (claimMessage.isDone ? (
          <p className="door__done" role="status">
            {claimMessage.text}
          </p>
        ) : (
          <Failure message={claimMessage.text} />
        ))}
    </>
  )
}

/** One author's import: what it is, and the three things a reader can do with it. */
function ImportedSection({
  entry,
  onOpen,
  onClaim,
  onRemove,
}: {
  entry: ImportSummary
  onOpen: (card: Card) => void
  onClaim: () => void
  onRemove: () => void
}): JSX.Element {
  return (
    <section className="imported">
      <h3>
        {t('imports.from', { author: entry.author })}
        <span className="muted"> · {t('imports.count', { count: entry.findings })}</span>
      </h3>
      <p className="muted">{t('imports.readOnly')}</p>
      <p>
        <button
          type="button"
          className="link"
          onClick={() => onOpen({ kind: 'imported', author: entry.author })}
        >
          {t('imports.open')}
        </button>
        <button type="button" className="link" onClick={onClaim}>
          {t('imports.claim')}
        </button>
        <button type="button" className="link" onClick={onRemove}>
          {t('imports.remove')}
        </button>
      </p>
    </section>
  )
}

/**
 * The feeds this reader follows.
 *
 * `available_version` differing from `installed_version` is not an error and not
 * a button: the sync runs in the background on sign-in, so the honest label is
 * "updating to", not "click to update".
 */
export function Subscriptions({ onChanged }: { onChanged: () => void }): JSX.Element | null {
  const { state, reload } = useLoaded<Subscription[]>(listSubscriptions, 'subscriptions')
  if (state.status !== 'ready' || state.data.length === 0) return null

  async function stop(following: Subscription) {
    if (!window.confirm(t('imports.confirmUnfollow', { author: following.author }))) return
    await unsubscribeFromFeed(following.id)
    reload()
    onChanged()
  }

  return (
    <section className="following">
      <h3>{t('imports.following')}</h3>
      <ul className="claims">
        {state.data.map((following) => (
          <li key={following.id} className="claim">
            <p className="claim__text">
              {following.author_name || following.author} · {following.title}
            </p>
            <p className="claim__meta muted">
              {following.installed_version === ''
                ? t('imports.neverSynced')
                : following.available_version !== null &&
                    following.available_version !== following.installed_version
                  ? t('imports.behind', { version: following.available_version })
                  : t('imports.upToDate', { version: following.installed_version })}
              <button type="button" className="link" onClick={() => void stop(following)}>
                {t('imports.unfollow')}
              </button>
            </p>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** One author's imported findings — read-only, and said so plainly. */
export function ImportedCard({
  author,
  corpus,
  onOpen,
}: {
  author: string
  corpus: string | null
  onOpen: (card: Card) => void
}): JSX.Element {
  const { state } = useLoaded<FindingPage>(
    () =>
      listFindings({
        corpus: corpus ?? undefined,
        imported_from: author,
        limit: PAGE_SIZE,
      }),
    `imported:${author}:${corpus}`,
  )

  if (state.status === 'loading') return <Loading />
  if (state.status === 'error') return <Failure message={state.message} />

  return (
    <article>
      <h2 className="card__kind">{t('imports.from', { author })}</h2>
      <p className="muted">{t('imports.readOnlyLong')}</p>
      <ol className="claims">
        {state.data.findings.map((finding) => (
          <li key={finding.id} className="claim">
            <p className="claim__text">
              <button
                type="button"
                className="link"
                onClick={() => onOpen({ kind: 'finding', id: finding.id })}
              >
                {finding.title}
              </button>
            </p>
            <p className="claim__meta muted">
              {t(`findings.status.${finding.status}`)}
              {/* Shown only where the server actually looked. `is_adopted` is
                  absent when nobody asked, which is not the same as false — so
                  the badge is missing rather than wrongly saying "not adopted". */}
              {finding.is_adopted === true && (
                <span className="badge"> {t('adopt.already')}</span>
              )}
              {citationLine(finding) && <span> · {citationLine(finding)}</span>}
            </p>
          </li>
        ))}
      </ol>
    </article>
  )
}
